import { IconAlertCircle, IconPlus } from '@tabler/icons-react';
import { Button } from '@xver/components/Atoms';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';
import usePageHeading from '@xver/hooks/usePageHeading';
import { useProjectRequestsQuery } from '@xver/hooks/useProjectRequestsQuery';
import { useProjectsQuery } from '@xver/hooks/useProjectsQuery';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import CreateProjectModal from './components/CreateProjectModal';
import ProjectBreadcrumbs from './components/ProjectBreadcrumbs';
import ProjectRequestsSection from './components/ProjectRequestsSection';
import ProjectsTable from './components/ProjectsTable';

export default function ProjectsPage() {
    const { t } = useTranslation('projects');
    const headingRef = usePageHeading(t('list.title'));
    const [createOpen, setCreateOpen] = useState(false);
    const { data: projects, isPending, isError, refetch, isRefetching } = useProjectsQuery();
    const { data: requests, pendingCount } = useProjectRequestsQuery();

    return (
        <div className="w-full min-h-full flex flex-col justify-start items-center gap-8">
            <div className="w-full flex flex-col justify-start items-start">
                <ProjectBreadcrumbs />
                <div className="w-full flex flex-col sm:flex-row justify-between sm:items-end gap-3">
                    <div className="flex flex-col justify-start items-start gap-1">
                        <h1 ref={headingRef} tabIndex={-1} className="text-3xl font-bold">
                            {t('list.title')}
                        </h1>
                        <p className="max-w-[80ch] text-base text-fg/68">{t('list.description')}</p>
                    </div>
                    <Button
                        size="lg"
                        leftIcon={<IconPlus size={16} aria-hidden="true" />}
                        onClick={() => setCreateOpen(true)}
                    >
                        {t('create.open')}
                    </Button>
                </div>
            </div>

            {requests && pendingCount > 0 && (
                <ProjectRequestsSection requests={requests} fallbackFocusRef={headingRef} />
            )}

            <div className="w-full">
                <DialogBox
                    label={t('list.tableTitle')}
                    description={t('list.tableDescription')}
                    classNames="p-0"
                >
                    {isError && !projects ? (
                        <div role="alert" className="w-full p-6 flex flex-col items-start gap-3">
                            <p className="flex items-center gap-2 text-sm text-fg">
                                <IconAlertCircle size={18} aria-hidden="true" />
                                {t('list.loadFailed')}
                            </p>
                            <Button
                                variant="secondary"
                                onClick={() => refetch()}
                                isLoading={isRefetching}
                            >
                                {t('common.retry')}
                            </Button>
                        </div>
                    ) : (
                        <ProjectsTable projects={projects} isLoading={isPending} />
                    )}
                </DialogBox>
            </div>

            <CreateProjectModal opened={createOpen} onClose={() => setCreateOpen(false)} />
        </div>
    );
}
